import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import { FiExternalLink } from 'react-icons/fi';
import S from './Widget.module.scss';
import WidgetHeaderControls from './WidgetHeaderControls';
import SwitchGraphe from '../../Graphe/AffichageGraphe/SwitchGraphe';
import Popup from '../../UI/PopupVue/Popup';
import * as actions from '../../../services/actions';
import * as glossaire_type_de_graphe from '../../../ressources/glossaires/glossaire_type_de_graphe';

const TABLEAU_TYPES = new Set([
  glossaire_type_de_graphe.tableau_dernieres_valeurs_et_totaux_rapport,
  glossaire_type_de_graphe.tableau_delta_des_bornes_et_totaux_rapport,
  glossaire_type_de_graphe.tableau_dernieres_valeurs_et_totaux_responsable,
  glossaire_type_de_graphe.tableau_delta_des_bornes_et_totaux,
  glossaire_type_de_graphe.tableau_dernieres_valeurs_et_totaux,
  glossaire_type_de_graphe.tableau_min_max_date,
  glossaire_type_de_graphe.tableau_absolute_min_max_date,
  glossaire_type_de_graphe.tableau_telt,
]);
const TABLEAU_SCALE = 0.6;

const WidgetGraphe = ({
  graphesFavoris = [],
  headerContent = null,
  showHeader = true,
  loading = false,
}) => {
  const dispatch = useDispatch();
  const donneesGraphes = useSelector(
    (state) => state.graphe.liste_donnees_graphes
  );

  const hasGraphes = graphesFavoris && graphesFavoris.length > 0;
  const [selectedGrapheIndex, setSelectedGrapheIndex] = useState('0');
  const graphLength = graphesFavoris?.length || 0;
  const [afficherPopupGraphe, setAfficherPopupGraphe] = useState(false);
  const ouvrirPopupGraphe = () => setAfficherPopupGraphe(true);
  const fermerPopupGraphe = () => setAfficherPopupGraphe(false);

  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({
    width: 400,
    height: 250,
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setContainerSize({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!hasGraphes) {
      setSelectedGrapheIndex('0');
      return;
    }

    const currentIndex = Number(selectedGrapheIndex);
    if (currentIndex >= graphLength) {
      setSelectedGrapheIndex('0');
    }
  }, [graphLength, hasGraphes, selectedGrapheIndex]);

  const grapheOptions = useMemo(() => {
    if (!hasGraphes || graphLength < 2) {
      return [];
    }

    return graphesFavoris.map((graphe, index) => ({
      value: String(index),
      label: graphe.graph_name || `${checkMot('graphe')} ${index + 1}`,
    }));
  }, [graphLength, graphesFavoris, hasGraphes]);

  const currentGraphe = hasGraphes
    ? graphesFavoris[Number(selectedGrapheIndex)] || graphesFavoris[0]
    : null;

  const [loadingData, setLoadingData] = useState(false);

  // Fetcher les données depuis l'API si absentes du store.
  // Si currentGraphe.config est disponible (données déjà enrichies par
  // reqRecupererAccueilSummary), on ne bloque pas l'affichage avec loadingData.
  useEffect(() => {
    if (!currentGraphe?.graph_id) return;
    const graphId = parseInt(currentGraphe.graph_id, 10);
    if (!donneesGraphes[graphId]) {
      if (!currentGraphe?.config) {
        // Aucune donnée disponible : afficher le spinner pendant le chargement
        setLoadingData(true);
      }
      dispatch(actions.reqRecupererDonneesGraphe(graphId));
    }
  }, [currentGraphe?.graph_id, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lire les données depuis le store Redux, avec fallback sur les données
  // déjà enrichies dans currentGraphe (issues de reqRecupererAccueilSummary).
  // Cela évite le spinner infini lors du premier chargement lorsque
  // donneesGraphes n'est pas encore peuplé pour ce graph_id.
  const storeData = currentGraphe?.graph_id
    ? (donneesGraphes[parseInt(currentGraphe.graph_id, 10)] ??
      (currentGraphe?.config
        ? {
            config: currentGraphe.config,
            data: currentGraphe.data ?? [],
            alarms_levels: currentGraphe.alarms_levels ?? [],
          }
        : null))
    : null;

  // Arrêter le loader quand les données arrivent
  useEffect(() => {
    if (storeData !== null) setLoadingData(false);
  }, [storeData]);

  const reqRecupererDonneesGraphe = (id) =>
    dispatch(actions.reqRecupererDonneesGraphe(id));

  const resolvedHeader =
    headerContent ||
    (showHeader ? (
      <WidgetHeaderControls
        titleKey="graphe"
        selectOptions={grapheOptions}
        selectedValue={selectedGrapheIndex}
        onSelectChange={setSelectedGrapheIndex}
        headerAction={
          currentGraphe?.graph_id ? (
            <button
              className={S.widgetMenuBtn}
              title={checkMot('voir_plus')}
              onClick={ouvrirPopupGraphe}
            >
              <FiExternalLink size={18} />
            </button>
          ) : null
        }
      />
    ) : null);

  const isTableauType =
    storeData?.config?.graph_type_id !== undefined &&
    TABLEAU_TYPES.has(storeData.config.graph_type_id);

  const switchGrapheProps =
    storeData && storeData.config
      ? {
          graph_id: currentGraphe?.graph_id,
          donneesGraphe: storeData,
          configuration: storeData.config,
          donnees: storeData.data,
          seuils: storeData.alarms_levels || [],
          reqRecupererDonneesGraphe,
          switchAfficherModifierIntervalle: () => {},
          dimensionWidth: isTableauType
            ? containerSize.width / TABLEAU_SCALE
            : containerSize.width,
          dimensionHeight: isTableauType
            ? containerSize.height / TABLEAU_SCALE
            : containerSize.height,
          minimaliste: true,
          viewobj_json_content: {
            layout: { showlegend: false },
            displayModeBar: false,
          },
        }
      : null;

  return (
    <>
      <div className={[S.widget, S.widgetMedium].join(' ')}>
        {resolvedHeader}
        <div className={S.widgetContentGraph} ref={containerRef}>
          {loading || loadingData ? (
            <div className={S.widgetLoadingState}>
              <div className={S.widgetLoadingSpinner} />
              <span className={S.widgetLoadingText}>
                {checkMot('chargement_en_cours')}
              </span>
            </div>
          ) : hasGraphes && currentGraphe && switchGrapheProps ? (
            <div className={S.grapheContainer}>
              {isTableauType ? (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transformOrigin: 'top left',
                    transform: `scale(${TABLEAU_SCALE})`,
                  }}
                >
                  <SwitchGraphe {...switchGrapheProps} />
                </div>
              ) : (
                <SwitchGraphe {...switchGrapheProps} />
              )}
            </div>
          ) : (
            <div className={S.widgetLoadingState}>
              <div className={S.widgetLoadingSpinner} />
              <span className={S.widgetLoadingText}>
                {checkMot('chargement_en_cours')}
              </span>
            </div>
          )}
        </div>
      </div>
      <Popup
        sansBouton={true}
        eventALaFermeture={fermerPopupGraphe}
        showModal={afficherPopupGraphe}
      >
        {afficherPopupGraphe && switchGrapheProps && (
          <SwitchGraphe
            {...switchGrapheProps}
            dimensionWidth={window.innerWidth - 100}
            dimensionHeight={window.innerHeight - 200}
            minimaliste={false}
          />
        )}
      </Popup>
    </>
  );
};

export default WidgetGraphe;
